import React, { useCallback } from 'react'
import {
  Box,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import rootStore from '../../../store/RootStore'
import { observer } from 'mobx-react'
import TreeView from '../../../components/features/Wizard/TreeView/TreeView'
import DrilldownSelector from '../../../components/features/Wizard/DrilldownSelector/DrilldownSelector'
import {
  useFetchChartListQuery,
  useFetchStructureQuery,
} from '../hooks/useFetchStructureQuery'
import { useQueryClient } from '@tanstack/react-query'

const SetupReportStructure = observer(() => {
  const {
    wizardStore: { setSelectedNodeId, loadChartSeries, updateReportBlueprint },
  } = rootStore

  const { onOpen, onClose, isOpen } = useDisclosure()

  const queryClient = useQueryClient()

  const urlSourceId = getURLSourceId()
  const {
    data: reportBlueprint,
    isLoading: reportBlueprintLoading,
    isError: reportBlueprintError,
  } = useFetchStructureQuery(urlSourceId)

  const {
    data: availableCharts,
    isLoading: availableChartsLoading,
    isError: availableChartsError,
  } = useFetchChartListQuery(urlSourceId, reportBlueprint?.id)

  const handleEditDrilldown = ({ id, type }) => {
    setSelectedNodeId(id)

    if (type === 'chart' || type.startsWith('drilldown')) {
      onOpen()

      /**
       * Load chart columns and rows information to add drilldowns links
       */
      loadChartSeries()
    }
  }

  /**
   * Display a highlight to the node
   * @param id
   * @param type
   */
  const handleItemClick = ({ id }) => {
    setSelectedNodeId(id)
  }

  const handleChangeStructure = useCallback(
    components => {
      queryClient.setQueryData(
        ['reportSourceStructure', urlSourceId],
        oldData => {
          return {
            ...oldData,
            components,
          }
        },
      )

      updateReportBlueprint(components)
    },
    [queryClient, urlSourceId, updateReportBlueprint],
  )

  if (reportBlueprintLoading || availableChartsLoading) {
    return <Loading />
  }

  if (reportBlueprintError || availableChartsError) {
    return <div>Something went wrong ...</div>
  }

  return (
    <Box>
      <TreeView
        onChangeStructure={handleChangeStructure}
        rootId={reportBlueprint?.id}
        reportBlueprint={reportBlueprint}
        availableCharts={availableCharts}
        onEditDrilldown={handleEditDrilldown}
        onItemClick={handleItemClick}
        readOnly={false}
      />
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader />
          <ModalCloseButton />
          <ModalBody>
            <DrilldownSelector />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  )
})
const getURLSourceId = () => {
  const {
    wizardStore: { setSourceId },
  } = rootStore
  const urlParams = new URLSearchParams(window.location.search)
  const urlSourceId = urlParams.get('sourceId')
  setSourceId(urlSourceId)
  return urlSourceId
}

const Loading = () => {
  const colorProps = {
    startColor: 'brand.lightGray',
    endColor: 'transparent',
  }
  return (
    <HStack>
      <VStack w="50%">
        <Skeleton
          w="100%"
          h="30px"
          {...colorProps}
        />
        <Skeleton
          w="100%"
          height="200px"
          {...colorProps}
        />
      </VStack>
      <VStack w="50%">
        <Skeleton
          w="100%"
          h="30px"
          {...colorProps}
        />
        <Skeleton
          w="100%"
          height="200px"
          {...colorProps}
        />
      </VStack>
    </HStack>
  )
}

// const useFetchStructure = () => {
//   const {
//     wizardStore: { fetchStructureData, sourceId, error, reportBlueprint },
//   } = rootStore
//   useEffect(() => {
//     if (reportBlueprint.children?.length > 0) return
//     const urlParams = new URLSearchParams(window.location.search)
//     const urlSourceId = urlParams.get('sourceId')
//     ;(async () => await fetchStructureData(urlSourceId))()
//   }, [fetchStructureData, sourceId, error, reportBlueprint])
// }

export default SetupReportStructure
